import * as React from "react";
import { cn } from "@/lib/utils";
import { Check, Clock, X, Lock, Loader2, AlertCircle } from "lucide-react";

type StatusType =
  | "pending"
  | "approved"
  | "rejected"
  | "in-progress"
  | "locked"
  | "completed"
  | "review"
  | "available";

interface StatusBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  status: StatusType;
  label?: string;
  showIcon?: boolean;
  size?: "sm" | "md" | "lg";
}

const statusConfig: Record<
  StatusType,
  { className: string; icon: React.ElementType; defaultLabel: string }
> = {
  pending: {
    className: "status-pending",
    icon: Clock,
    defaultLabel: "Pending",
  },
  approved: {
    className: "status-approved",
    icon: Check,
    defaultLabel: "Approved",
  },
  rejected: {
    className: "status-rejected",
    icon: X,
    defaultLabel: "Rejected",
  },
  "in-progress": {
    className: "status-in-progress",
    icon: Loader2,
    defaultLabel: "In Progress",
  },
  locked: {
    className: "status-locked",
    icon: Lock,
    defaultLabel: "Locked",
  },
  completed: {
    className: "status-completed",
    icon: Check,
    defaultLabel: "Completed",
  },
  review: {
    className: "status-pending",
    icon: AlertCircle,
    defaultLabel: "Under Review",
  },
  available: {
    className: "status-approved",
    icon: Check,
    defaultLabel: "Available",
  },
};

const StatusBadge = React.forwardRef<HTMLDivElement, StatusBadgeProps>(
  ({ className, status, label, showIcon = true, size = "md", ...props }, ref) => {
    const config = statusConfig[status];
    const Icon = config.icon;

    const sizeClasses = {
      sm: "px-2 py-0.5 text-xs",
      md: "px-3 py-1 text-sm",
      lg: "px-4 py-1.5 text-base",
    };

    const iconSizeClasses = {
      sm: "h-3 w-3",
      md: "h-4 w-4",
      lg: "h-5 w-5",
    };

    return (
      <div
        ref={ref}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full font-medium",
          config.className,
          sizeClasses[size],
          className
        )}
        {...props}
      >
        {showIcon && (
          <Icon
            className={cn(
              iconSizeClasses[size],
              status === "in-progress" && "animate-spin"
            )}
          />
        )}
        <span>{label || config.defaultLabel}</span>
      </div>
    );
  }
);
StatusBadge.displayName = "StatusBadge";

export { StatusBadge, statusConfig };
export type { StatusBadgeProps, StatusType };
